import "./list-employee-attachment.scss";

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "primereact/button";
import { Paginator, PaginatorPageChangeEvent } from "primereact/paginator";
import { DataTable, DataTableStateEvent } from "primereact/datatable";
import { Column } from "primereact/column";
import { Sidebar } from "primereact/sidebar";
import { CiSearch } from "react-icons/ci";
import { MdClear } from "react-icons/md";

import SkeletonList from "@shared/ui/skeleton-list/skeleton-list";
import ErrorSection from "@shared/ui/error-section/error-section";
import SearchInput from "@shared/ui/search-input/search-input";
import HeaderContent from "@shared/ui/header-content/header-content";
import { useQyGetEmployeeAttachment } from "@core/query/employee-attachment.query";
import {
  GetEmployeeAttachmentModel,
  GetResponseEmployeeAttachmentModel,
} from "@core/model/query-model-employee-attachment";
import { TableSort } from "@core/model/table-sort";
import { FilterEmployeeAttachment } from "../filter-employee-attachment/filter-employee-attachment";
import { useFilterEmployeeAttachmentContext } from "../filter-employee-attachment/filter-employee-attachment.context";
import { viewAsset } from "@core/query/google-cloud-storage.query";
import { Avatar } from "primereact/avatar";

export function EmployeeAttachment() {
  // THIS IS THE LOCAL DECLARATION
  const navigate = useNavigate();
  const [rowLimit, setRowLimit] = useState(5);
  const [pageNumber, setPageNumber] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [first, setFirst] = useState(0);
  const [tableSort, setTableSort] = useState<TableSort>({
    sortField: undefined,
    sortOrder: undefined,
  });
  const [sort, setSort] = useState({});
  const [filterPanel, setFilterPanel] = useState(false);
  const { filterEntity } = useFilterEmployeeAttachmentContext();
  const [responseData, setResponseData] = useState<
    GetEmployeeAttachmentModel[]
  >([]);

  // THIS IS THE QUERY DECLARATION
  const handleEmploymentApiSuccess = async (
    response: GetResponseEmployeeAttachmentModel
  ) => {
    const _responseData = response.data || [];
    if (_responseData.length > 0) {
      const updatedResponse = await Promise.all(
        _responseData.map(async (element) => {
          const elementFile = await viewAsset(element.file);
          const fileBlob = URL.createObjectURL(elementFile);
          return { ...element, fileBlob };
        })
      );
      setResponseData(updatedResponse);
    }
  };

  const {
    data: employeeAttachmentResponse,
    isLoading,
    isError,
    error,
  } = useQyGetEmployeeAttachment(
    searchTerm,
    rowLimit,
    pageNumber,
    sort,
    filterEntity,
    undefined,
    undefined,
    undefined,
    true,
    handleEmploymentApiSuccess
  );

  // THIS IS THE LOCAL DECLARATION
  const handleSearch = (searchTerm: string) => {
    setSearchTerm(searchTerm);
  };
  const handleTableSort = (e: DataTableStateEvent) => {
    setTableSort({
      sortField: e.sortField,
      sortOrder: e.sortOrder,
    });
    const order = e.sortOrder === 1 ? "ASC" : "DESC";
    setSort({ [e.sortField]: order });
  };
  const navigateToRecord = (item: GetEmployeeAttachmentModel) => {
    navigate(`${item.code}`);
  };
  const onPageChange = (event: PaginatorPageChangeEvent) => {
    const offsetValue = event.page * rowLimit;
    setFirst(event.first);
    setPageNumber(offsetValue);
    setRowLimit(event.rows);
  };
  const handleNew = () => {
    navigate(`new`);
  };

  // THIS IS THE LOCAL RENDERS
  const displayLoading = (
    <div className="card">
      <SkeletonList count={4} />
    </div>
  );
  const displayError = (
    <div className="card">
      <ErrorSection title="Error Occured" message={(error as any)?.message} />
    </div>
  );
  const filterElement = (
    <section>
      <div className="flex gap-4 items-center mb-2">
        <SearchInput
          clearIcon={<MdClear />}
          searchIcon={<CiSearch />}
          searchTerm={searchTerm}
          onSearch={handleSearch}
          placeholder="Search items"
          className="w-full block"
        />
        <Button
          label="Filter"
          severity="secondary"
          badgeClassName="p-badge-danger"
          onClick={() => setFilterPanel(true)}
          outlined
        />
      </div>

      <Sidebar visible={filterPanel} onHide={() => setFilterPanel(false)}>
        <FilterEmployeeAttachment />
      </Sidebar>
    </section>
  );
  const grid = (
    <section className="mt-6">
      <h5 className="mb-2">
        Total: <b>{employeeAttachmentResponse?.count}</b>
      </h5>

      <DataTable
        value={responseData}
        selectionMode="single"
        onSelectionChange={(e) => navigateToRecord(e.value)}
        onSort={handleTableSort}
        sortField={tableSort.sortField}
        sortOrder={tableSort.sortOrder}
      >
        <Column
          field="attachment_type_name"
          header="Attachment Type"
          sortable
        ></Column>
        <Column
          body={(cell) => (
            <Avatar
              image={cell.fileBlob}
              style={{ width: "100px", height: "100px" }}
            />
          )}
          header="File"
          sortable
        ></Column>
        <Column field="mime_type" header="Mime Type" sortable></Column>
      </DataTable>

      <Paginator
        first={first}
        rows={rowLimit}
        totalRecords={employeeAttachmentResponse?.count}
        rowsPerPageOptions={[10, 20, 30]}
        onPageChange={onPageChange}
      />
    </section>
  );

  return (
    <div id="EmployeeAttachment" className="employee-attachment">
      <HeaderContent title="Employee Attachment">
        <span className="flex gap-2">
          <Button label="New" onClick={handleNew} />
        </span>
      </HeaderContent>

      <div className="p-7">
        {filterElement}
        {isLoading && displayLoading}
        {isError && !isLoading && displayError}
        {!isLoading && !isError && grid}
      </div>
    </div>
  );
}

export default EmployeeAttachment;
